import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./File.module.css";
import Button from "./Button";
import useFetch from "@maxis/client-logic/hooks/useFetch";
import TextInput from "./TextInput";

/**
 * FileSingle
 * @type React.FC<FileSinglePropTypes>
 */
const FileSingle = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(FileSinglePropTypes).includes(p)) {
      console.error(`FileSingle: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /**
   * FileSingle Component의 State
   */
  const [currState, setCurrState] = useState({
    readOnly: props.readOnly,
    attachSeq: null,
    attachFileSeq: null,
    fileToUpload: null,
    saveInDB: props.saveInDB,
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 파일 목록 조회 */
    loadFiles(attachSeq) {
      _loadFileListByAttachSeq(attachSeq);
    },
    setReadOnly(b) {
      setCurrState((prev) => ({ ...prev, readOnly: b }));
    },
    /** 파일 저장 */
    async saveFiles(publicPath = false, requiredFile = true) {
      if (currState.attachSeq && currState.attachFileSeq) {
        return currState.attachSeq;
      }

      const param = {
        ATTACH_SEQ: currState.attachSeq || "",
        SINGLE_ATTACH_MODE: true,
        LAST_ORD: 0,
        SAVE_IN_DB: currState.saveInDB,
        PUBLIC_PATH: publicPath,
        REQUIRED_FILE: requiredFile,
      };
      const response = await request("/file-upload", {
        body: param,
        attachFiles: [currState.fileToUpload],
      });

      if (response) {
        return response.ATTACH_SEQ;
      }
    },

    getAttachSeq() {
      return currState.attachSeq;
    },

    getAttachFiles() {
      return [currState.fileToUpload];
    },

    /** 초기화 */
    clear() {
      refFilename.current.setValue("");
      if (refFileInput.current.value !== null) refFileInput.current.value = "";

      setCurrState({
        readOnly: props.readOnly,
        attachSeq: null,
        attachFileSeq: null,
        fileToUpload: null,
      });
    },
  }));

  const { request, downloadFile, partialDownloadFile } = useFetch();
  const refFileInput = useRef();

  /**
   * 파일 추가
   * @param {*} files input으로부터 선택된 file 목록
   */
  const _onFileAdd = (files) => {
    if (files.length > 0) {
      setCurrState((prev) => ({
        ...prev,
        attachFileSeq: null,
        fileToUpload: files[0],
      }));
      refFilename.current.setValue(files[0].name);
    } else {
      setCurrState((prev) => ({
        ...prev,
        attachFileSeq: null,
        fileToUpload: null,
      }));
      refFilename.current.setValue("");
    }
  };

  const _downloadFile = () => {
    downloadFile(currState.attachFileSeq, currState.saveInDB);
    //partialDownloadFile(currState.attachFileSeq, currState.saveInDB);
  };

  /**
   * 파일 목록 조회
   * @param {*} attachSeq
   */
  const _loadFileListByAttachSeq = async (attachSeq) => {
    const param = {
      ATTACH_SEQ: attachSeq,
    };
    const response = await request("/file-list", {
      body: param,
    });

    const files = response ?? [];
    if (files.length > 0) {
      refFilename.current.setValue(files[0].ORG_FILE_NM);
      setCurrState((prev) => ({
        ...prev,
        attachSeq: attachSeq,
        attachFileSeq: files[0].ATTACH_FILE_SEQ,
      }));
    } else {
      refFilename.current.setValue("");
      setCurrState((prev) => ({
        ...prev,
        attachSeq: attachSeq,
        attachFileSeq: null,
      }));
    }
  };

  const _deleteFile = () => {
    refFilename.current.setValue("");
    setCurrState((prev) => ({
      ...prev,
      attachFileSeq: null,
      fileToUpload: null,
    }));

    if (refFileInput.current.value !== null) refFileInput.current.value = "";
  };

  const refFilename = useRef();

  return (
    <div className={styles.e3File}>
      <input
        style={{ display: "none" }}
        id="contentList"
        ref={refFileInput}
        type="file"
        accept={props.acceptValue}
        onChange={(e) => _onFileAdd(e.target.files)}
      />

      <div style={{ display: "flex", alignItems: "center" }}>
        <TextInput ref={refFilename} readOnly={true} controlWidth="wpct_100" disabled={true} />
        {!currState.readOnly ? (
          <Button
            label={"파일선택"}
            colorType="sub1"
            authBtn={"SEARCH"}
            checkAuth={true}
            onClick={() => {
              refFileInput.current?.click();
            }}
          />
        ) : null}
        {currState.attachFileSeq ? (
          <Button label={"다운로드"} authBtn={"DOWNLOAD"} checkAuth={true} onClick={_downloadFile} />
        ) : null}
        {!currState.readOnly && (currState.attachFileSeq || currState.fileToUpload) ? (
          <Button label={"삭제"} authBtn={"DELETE"} checkAuth={true} onClick={_deleteFile} />
        ) : null}
      </div>
    </div>
  );
});

const FileSinglePropTypes = {
  readOnly: PropTypes.bool,
  saveInDB: PropTypes.bool,
  acceptValue: PropTypes.string,
};

FileSingle.propTypes = FileSinglePropTypes;
FileSingle.defaultProps = {
  readOnly: false,
  saveInDB: false,
  acceptValue: "*.*",
};
FileSingle.displayName = "FileSingle";
export default FileSingle;
